///
/// EarthBrowser
///
/// Created by Matt Giger on 11/14/2023
/// Copyright (c) 2023 Geodata Labs
///

import { StovokorContext } from "@stovokor/StovokorContext"
import { type DrawMode } from "@stovokor/shader/GLConstants"
import { type ShaderAction } from "@stovokor/shader/ShaderActions"
import { adjustBufferMemory } from "@stovokor/shader/TextureMemory"
import { type VertexAttribute } from "@stovokor/shader/VertexAttribute"

export class VertexBuffer {
	public stride: number
	public vertexCount: number = 0
	/**
	 * Incremented whenever a new GL buffer object is created. A VertexArray
	 * records the generations of its buffers and rebuilds when one changes,
	 * since its attribute pointers reference the old object
	 */
	public generation: number = 0
	/**
	 * GL usage hint. Buffers refilled every frame (instance data) should
	 * use DYNAMIC_DRAW; a re-upload of the same byte length then goes
	 * through bufferSubData into the existing storage instead of
	 * reallocating it
	 */
	public usage: GLenum = WebGL2RenderingContext.STATIC_DRAW
	private buffer: WebGLBuffer | null = null
	private _data?: AllowSharedBufferSource
	private allocatedBytes: number = 0
	private _attributes: VertexAttribute[] = []
	protected glType: GLenum = 0x1406 // WebGL2RenderingContext.FLOAT
	protected typeSize: number = 4
	private context: StovokorContext

	constructor(context: StovokorContext, attributes: VertexAttribute[], data?: AllowSharedBufferSource) {
		this.context = context
		this.stride = 0
		this.vertexCount = 0
		this._attributes = attributes
		this._data = data

		for(const attribute of attributes) {
			attribute.buffer = this
			attribute.offset = this.stride
			this.stride += attribute.size

			if(attribute.count > 0) {
				context.logger.assert(this.vertexCount == 0 || this.vertexCount == attribute.count, "Vertex attribute count mismatch")
				this.vertexCount = attribute.count
			}
		}
	}

	public destroy(): void {
		if(this.buffer) {
			this.context.gl.deleteBuffer(this.buffer)
			this.buffer = null
			this._data = undefined
			this.account(0)
		}
	}

	/** Mirror this buffer's GPU bytes into the global memory counters */
	private account(bytes: number): void {
		if(bytes !== this.allocatedBytes) {
			adjustBufferMemory(bytes - this.allocatedBytes, (bytes > 0 ? 1 : 0) - (this.allocatedBytes > 0 ? 1 : 0))
			this.allocatedBytes = bytes
		}
	}

	public uploadData(vertexData: Float32Array | Uint16Array | Uint8Array): void {
		this._data = vertexData
		this.vertexCount = vertexData.length / this.stride
	}

	/** True while data is waiting to go to the GPU on the next bind */
	public get pending(): boolean {
		return this._data !== undefined || (this._attributes.length > 0 && !this._attributes[0].empty)
	}

	// Use subclasses to build non-float buffers
	protected buildBuffer(): AllowSharedBufferSource {
		if(this._attributes.length == 1) {
			const attribute = this._attributes[0]
			attribute.glType = this.glType
			attribute.typeSize = this.typeSize
			const data = this.buildArrayBuffer(attribute.values)
			attribute.clearVertices()
			return data
		} else {
			const data = this.buildArrayBufferLength(this.stride * this.vertexCount)
			this._attributes.forEach(attribute => this.interleave(attribute, data))
			return data
		}
	}

	protected buildArrayBufferLength(length: number): Float32Array | Uint16Array | Uint8Array {
		const data = new Float32Array(length)
		return data
	}

	protected buildArrayBuffer(values: number[]): Float32Array | Uint16Array | Uint8Array {
		const data = new Float32Array(values)
		return data
	}

	protected interleave(attribute: VertexAttribute, data: Float32Array | Uint16Array | Uint8Array): void {

		attribute.glType = this.glType
		attribute.typeSize = this.typeSize

		let offset = attribute.offset
		const size = attribute.size

		// Create a typed array view of the values for more efficient access
		const valuesTyped = this.buildArrayBuffer(attribute.values)

		// Process vertices in groups of 'size'
		const vertexCount = valuesTyped.length / size
		for (let vertexIndex = 0; vertexIndex < vertexCount; vertexIndex++) {
			const valueIndex = vertexIndex * size

			// Create a view of the source data for this vertex
			const sourceView = valuesTyped.subarray(valueIndex, valueIndex + size)

			// Create a view of the destination at the current offset
			// This allows us to copy directly to the specific position in the interleaved buffer
			const destView = data.subarray(offset, offset + size)

			// Copy the data - this is more efficient than using set() on the full array
			destView.set(sourceView)

			// Move to the next vertex position in the interleaved buffer
			offset += this.stride
		}

		attribute.clearVertices()
	}

	public bind(): void {
		if(this.buffer === null) {
			this.buffer = this.context.gl.createBuffer()
			this.generation++
		}

		if(this._data) {
			// if the data is supplied, upload it
			this.context.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, this.buffer)
			if(this.usage !== WebGL2RenderingContext.STATIC_DRAW && this._data.byteLength === this.allocatedBytes && this.allocatedBytes > 0) {
				this.context.gl.bufferSubData(WebGL2RenderingContext.ARRAY_BUFFER, 0, this._data)
			} else {
				this.context.gl.bufferData(WebGL2RenderingContext.ARRAY_BUFFER, this._data, this.usage)
			}
			this.account(this._data.byteLength)
			this._data = undefined	// free up memory
			// Any attribute values still pending would otherwise be built
			// into the buffer on the next bind, overwriting this upload
			this._attributes.forEach(attribute => attribute.clearVertices())

		} else if(this._attributes.length > 0 && !this._attributes[0].empty) {
			// if the first attribute has values, we need to upload the data

			// first validate our vertex count is uniform
			for(const attribute of this._attributes) {
				if(this.vertexCount === 0) {
					this.vertexCount = attribute.count
				} else if(this.vertexCount !== attribute.count) {
					throw new Error("Vertex attribute count mismatch")
				}
			}

			const data = this.buildBuffer()

			this.context.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, this.buffer)
			this.context.gl.bufferData(WebGL2RenderingContext.ARRAY_BUFFER, data, this.usage)
			this.account(data.byteLength)

		} else {
			this.context.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, this.buffer)
		}
	}

	public unbind(): void {
		this.context.gl.bindBuffer(WebGL2RenderingContext.ARRAY_BUFFER, null)
	}

	// For indexless rendering
	public draw(mode: DrawMode): void {
		this.context.gl.drawArrays(mode, 0, this.vertexCount)
	}

	public drawAction(mode: DrawMode): ShaderAction {
		return () => this.context.gl.drawArrays(mode, 0, this.vertexCount)
	}

	/** Instanced draw; the count is read when the action runs, so it may change per frame */
	public drawInstancedAction(mode: DrawMode, instanceCount: () => number): ShaderAction {
		return () => {
			const instances = instanceCount()
			if(instances > 0) {
				this.context.gl.drawArraysInstanced(mode, 0, this.vertexCount, instances)
			}
		}
	}
}

export class VertexBufferUint8 extends VertexBuffer {
	constructor(context: StovokorContext, attributes: VertexAttribute[]) {
		super(context, attributes)
		this.glType = WebGL2RenderingContext.UNSIGNED_BYTE
		this.typeSize = 1
		// Type the attributes up front: buildBuffer only runs when the buffer
		// is built from attribute values, so a buffer first filled through
		// uploadData would otherwise bind its bytes as floats
		for(const attribute of attributes) {
			attribute.glType = this.glType
			attribute.typeSize = this.typeSize
		}
	}

	protected buildArrayBufferLength(length: number): Uint8Array {
		const data = new Uint8Array(length)
		return data
	}

	protected buildArrayBuffer(values: number[]): Float32Array | Uint16Array | Uint8Array {
		const data = new Uint8Array(values)
		return data
	}
}
